/**
 * State for the service-mapping backend.
 *
 * Owns the service store, the active locale, the current selection, and the
 * change history (see ./history.ts). Persistence is localStorage only.
 *
 * Scoped per team: dbId namespaces every key this hook touches. Passing a
 * different dbId (e.g. on a team switch) reloads the store and history from
 * that team's own keys.
 */

import { useStaging } from "../shared/useStaging"
import { useState, useEffect, useCallback, useRef } from "react"
import * as db from "./store"
import { toast } from "sonner"
import { type CmsAccess, checkServiceWrite, isServiceLive } from "../shared/permissions"
import { StoreSync, type SyncStatus } from "../shared/persistence/syncBridge"
import { makeServiceMapAdapter } from "../shared/persistence/serviceMapAdapter"
import { containerId } from "../shared/persistence/types"
import { registerHistorySync, pullHistory } from "../shared/persistence/historySync"
import {
  type ServiceStore, type EntityKind, emptyServiceStore, displayName, BUCKET,
} from "./types"
import { describeEdit } from "../shared/useRecordDraft"
import {
  type ChangeEntry, type ChangeAction, type EntityVersion,
  loadHistory, saveHistory, makeEntry, makeBulkEntry, revertEntry, clearHistory,
  versionsForEntity, restoreEntityVersion,
} from "./history"

const LOCALE_KEY_PREFIX = "svc-cms-locale"
const DEFAULT_DB = "default"

// The store's record buckets, which unpublished changes cover
const SERVICE_BUCKETS = Object.values(BUCKET) as string[]

// A service is on the public site once any language is published; other records always are
const serviceRecordIsLive = (bucket: string, s: any) => bucket !== "services" || isServiceLive(s)
const kindOfBucket = (bucket: string) =>
  (Object.keys(BUCKET) as (keyof typeof BUCKET)[]).find(k => BUCKET[k] === bucket)!

function localeKeyFor(dbId: string) { return dbId === DEFAULT_DB ? LOCALE_KEY_PREFIX : `${LOCALE_KEY_PREFIX}:${dbId}` }

export interface ServiceSelection {
  kind: EntityKind
  id: string
}

/** Identity recorded against each change, so the log shows who did what. */
export interface ServiceCMSAuthor {
  name?: string
  email?: string
  id?: string
  /** Model id, recorded when the agent is the one acting. */
  model?: string
}

export function useServiceCMS(author?: ServiceCMSAuthor, dbId: string = DEFAULT_DB, access: CmsAccess = "manage") {
  const syncRef = useRef<StoreSync<ServiceStore>>(null as any)
  if (!syncRef.current) syncRef.current = new StoreSync<ServiceStore>()
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(() => ({
    state: "idle", lastSyncedAt: null, error: null, pending: false,
  }))
  // The team whose rows have loaded; until then the store is a placeholder
  const [loadedFor, setLoadedFor] = useState("")

  const [store, setStoreState] = useState<ServiceStore>(db.emptyStoreFor)
  const [locale, setLocale] = useState<string>(() => localStorage.getItem(localeKeyFor(dbId)) || store.defaultLocale)
  const [selected, setSelected] = useState<ServiceSelection | null>(null)
  const [history, setHistoryState] = useState<ChangeEntry[]>(() => loadHistory(dbId))

  // Mirror the store (and history/author) in refs so tool closures read
  // current values: an agent turn makes many sequential tool calls without
  // re-rendering, so a captured snapshot would go stale after the first
  // mutation.
  const storeRef = useRef(store)
  // Edits to existing records wait as unpublished changes; the live store is what the database and public sites have
  const staging = useStaging<ServiceStore>("services", SERVICE_BUCKETS, store, serviceRecordIsLive)
  const historyRef = useRef(history)
  const authorRef = useRef(author)
  staging.setAuthor(() => ({ name: authorRef.current?.name, email: authorRef.current?.email }))
  const accessRef = useRef(access)
  accessRef.current = access
  useEffect(() => { authorRef.current = author }, [author])
  useEffect(() => { storeRef.current = store }, [store])
  useEffect(() => { historyRef.current = history }, [history])
  useEffect(() => { localStorage.setItem(localeKeyFor(dbId), locale) }, [dbId, locale])
  // dbId is read inside callbacks the same way as author — a ref avoids
  // re-creating every mutation callback whenever the team changes mid-render.
  const dbIdRef = useRef(dbId)

  // Team switch: reload the store and history from the new dbId's own keys
  // rather than continuing to show the previous team's data.
  useEffect(() => {
    if (dbIdRef.current === dbId) return
    dbIdRef.current = dbId
    // Cleared, not cached: the hydration effect below pulls this team's
    // real store from Postgres.
    const fresh = db.emptyStoreFor()
    staging.reset(fresh, dbId)
    storeRef.current = fresh
    setStoreState(fresh)
    const freshHistory = loadHistory(dbId)
    historyRef.current = freshHistory
    setHistoryState(freshHistory)
    setLocale(localStorage.getItem(localeKeyFor(dbId)) || fresh.defaultLocale)
    setSelected(null)
  }, [dbId])

  /** Single write path: React state, the ref, and localStorage together. */
  /*
   * Attach the database and hydrate from it. localStorage is a read-through
   * cache only; the database is authoritative.
   */
  useEffect(() => {
    const sync = syncRef.current
    // No team yet (still loading, or signed out) means there is nothing to
    // scope a query by -- not a fallback mode.
    if (!dbId) {
      sync.configure(null, "")
      registerHistorySync("service-map", null)
      return
    }
    let cancelledHistory = false
    // The change log is team-scoped, so it keys off the team id directly
    // rather than the derived container id the entity tables use.
    registerHistorySync("service-map", dbId)

    /*
     * Pull the change log too. Without this the log is write-only: entries
     * reach the database but every client reads its own localStorage copy,
     * so you cannot see -- or revert -- a change made from another browser.
     */
    void pullHistory("service-map", dbId).then(remote => {
      if (cancelledHistory || !remote) return
      historyRef.current = remote as ChangeEntry[]
      setHistoryState(remote as ChangeEntry[])
    })
    // The container row's PK is derived, not the bare team id -- see containerId.
    const container = containerId("service-map", dbId)
    sync.configure(makeServiceMapAdapter(dbId), container)
    staging.reset(storeRef.current, dbId)

    let cancelled = false
    void (async () => {
      const result = await sync.pull()
      if (cancelled) return

      if (!result.ok) {
        /*
         * The load failed, so whatever is in memory came from the local cache
         * and may be stale. Block writes rather than letting a later edit
         * overwrite the database with it.
         */
        sync.blockWrites("Could not load the service map: " + result.error)
        return
      }

      // An empty team keeps what's in memory, which the first save seeds the database with
      const working = await staging.hydrate(result.store ?? storeRef.current)
      if (cancelled) return
      storeRef.current = working
      setStoreState(working)
      setLoadedFor(dbId)
      // result.empty means this team has no rows yet: keep what is in memory,
      // which the first save seeds the database with.
    })()
    return () => { cancelled = true; cancelledHistory = true }
  }, [dbId])

  useEffect(() => syncRef.current.subscribe(setSyncStatus), [])

  // A pending write should survive a tab close where possible. Fire-and-forget:
  // beforeunload cannot await, which is why the local copy stays authoritative.
  useEffect(() => {
    const onLeave = () => { void syncRef.current.flush() }
    window.addEventListener("beforeunload", onLeave)
    return () => {
      window.removeEventListener("beforeunload", onLeave)
      void syncRef.current.flush()
    }
  }, [])

  const setStore = useCallback((next: ServiceStore) => {
    const access = accessRef.current
    const staged = staging.split(storeRef.current, next, access === "manage")
    // View-only can't change anything; otherwise only what goes live is checked against the role
    const denied = access === "view"
      ? checkServiceWrite(storeRef.current, next, access)
      : checkServiceWrite(staging.live(), staged.live, access)
    if (denied) {
      toast.error(denied, { id: "cms-denied" }) // one toast, not one per keystroke
      throw new Error(denied)
    }
    staging.commit(staged)
    storeRef.current = next
    setStoreState(next)
    // Only live changes go to the content tables; unpublished ones are saved by staging
    if (staged.liveChanged) syncRef.current.push(staged.live)
  }, [])


  const pushHistory = useCallback((entry: ChangeEntry) => {
    const next = [...historyRef.current, entry]
    historyRef.current = next
    setHistoryState(next)
    saveHistory(next, dbIdRef.current)
  }, [])

  /**
   * Records a change. Call with the store as it was BEFORE the mutation so the
   * snapshot is the pre-change state — that's what revert restores.
   */
  const record = useCallback((args: {
    prevStore: ServiceStore
    action: ChangeAction
    actor: "agent" | "user"
    entityKind: EntityKind
    entityId: string
    summary: string
    locale?: string
    /** Store after the change, for the `after` snapshot. Omit for deletes. */
    nextStore?: ServiceStore
  }) => {
    const { prevStore, nextStore, action, actor, entityKind, entityId, summary } = args

    // For a create there is no prior entity, so read identity from the new store.
    const identitySource = action === "create" ? (nextStore ?? prevStore) : prevStore
    const before = action === "create"
      ? undefined
      : JSON.parse(JSON.stringify(db.getEntity(prevStore, entityKind, entityId)))
    if (action !== "create" && !before) return // nothing to record against

    const afterEntity = nextStore ? db.getEntity(nextStore, entityKind, entityId) : null
    const entityForName = afterEntity ?? before
    const entityName = entityForName
      ? displayName(entityForName, args.locale ?? identitySource.defaultLocale, identitySource.defaultLocale)
      : entityId

    const who = authorRef.current
    pushHistory(makeEntry({
      action, actor, entityKind, entityId, entityName,
      authorName: who?.name,
      authorEmail: who?.email,
      authorId: who?.id,
      viaModel: actor === "agent" ? who?.model : undefined,
      locale: args.locale,
      summary,
      before,
      after: afterEntity ? JSON.parse(JSON.stringify(afterEntity)) : undefined,
    }))
  }, [pushHistory])

  /**
   * Records many changes with ONE history flush.
   *
   * record() calls saveHistory per entry, which JSON.stringifies up to 200
   * entries (each carrying full before/after entity snapshots) and writes
   * localStorage synchronously. In a batch that is N serializations of a
   * payload that only grows — the dominant cost of a large seed or translate
   * run, alongside the per-write store clone that setTranslationsBulk removes.
   *
   * Callers pass the `before` entity explicitly, because a batch shares one
   * mutable store: reading `before` from it after the write (as record() does)
   * would capture post-write state and silently corrupt revert.
   */
  const recordMany = useCallback((entries: Array<{
    action: ChangeAction
    actor: "agent" | "user"
    entityKind: EntityKind
    entityId: string
    entityName: string
    summary: string
    locale?: string
    before?: Record<string, any>
    after?: Record<string, any>
  }>) => {
    if (!entries.length) return
    const who = authorRef.current
    const made = entries.map(e => makeEntry({
      action: e.action, actor: e.actor,
      entityKind: e.entityKind, entityId: e.entityId, entityName: e.entityName,
      authorName: who?.name,
      authorEmail: who?.email,
      authorId: who?.id,
      viaModel: e.actor === "agent" ? who?.model : undefined,
      locale: e.locale,
      summary: e.summary,
      before: e.before,
      after: e.after,
    }))
    const next = [...historyRef.current, ...made]
    historyRef.current = next
    setHistoryState(next)
    saveHistory(next, dbIdRef.current)
  }, [])

  /**
   * Logs a bulk operation (a CSV/Excel import) as one line, with no single
   * entity attached — see makeBulkEntry. Not individually restorable; that
   * tradeoff is deliberate, since capturing a before-snapshot per touched
   * entity would reintroduce the per-row cloning cost applyImport was written
   * to avoid.
   */
  const recordBulk = useCallback((summary: string) => {
    const who = authorRef.current
    pushHistory(makeBulkEntry({
      actor: "agent",
      authorName: who?.name, authorEmail: who?.email, authorId: who?.id,
      viaModel: who?.model,
      summary,
    }))
  }, [pushHistory])

  // ── UI mutations (actor: "user") ─────────────────────────────────────────

  // Managers publish: unpublished changes go live, each as one version
  const publishPending = useCallback((keys?: string[]) => {
    if (accessRef.current !== "manage") {
      toast.error("Only a manager can publish changes.", { id: "cms-denied" })
      return
    }
    // Only changes that are still waiting
    const chosen = (keys ?? staging.allKeys()).filter(k => staging.pendingEntity(k) !== undefined)
    if (!chosen.length) return
    const prevLive = staging.live()
    const plan = staging.publishPlan(chosen)
    syncRef.current.push(plan.live)
    staging.applyPlan(plan, chosen)
    storeRef.current = plan.working
    setStoreState(plan.working)
    for (const key of chosen) {
      const { bucket, id } = staging.splitKey(key)
      const before = (prevLive as any)[bucket]?.[id]
      const after = (plan.live as any)[bucket]?.[id]
      if (!before || !after) continue
      record({ prevStore: prevLive, nextStore: plan.live, action: "update", actor: "user", entityKind: kindOfBucket(bucket), entityId: id, summary: `Published: ${describeEdit(before, after).replace(/^Edited /, "")}` })
    }
  }, [record])

  // Throws unpublished changes away; the record goes back to what's live
  const discardPending = useCallback((keys: string[]) => {
    if (accessRef.current === "view" || !keys.length) return
    const prevWorking = storeRef.current
    const plan = staging.discardPlan(keys)
    staging.applyPlan(plan, keys)
    storeRef.current = plan.working
    setStoreState(plan.working)
    for (const key of keys) {
      const { id, bucket } = staging.splitKey(key)
      if (!(plan.working as any)[bucket]?.[id]) continue
      record({ prevStore: prevWorking, nextStore: plan.working, action: "update", actor: "user", entityKind: kindOfBucket(bucket), entityId: id, summary: "Discarded unpublished changes" })
    }
  }, [record])

  const create = useCallback((kind: EntityKind, fields: Record<string, any>) => {
    const prev = storeRef.current
    const { store: next, id } = db.createEntity(prev, kind, fields, { locale })
    setStore(next)
    record({
      prevStore: prev, nextStore: next, action: "create", actor: "user",
      entityKind: kind, entityId: id, locale,
      summary: `Created ${kind} "${fields.name ?? id}"`,
    })
    setSelected({ kind, id })
    return id
  }, [locale, setStore, record])

  const update = useCallback((kind: EntityKind, id: string, fields: Record<string, any>) => {
    const prev = storeRef.current
    const next = db.updateEntity(prev, kind, id, fields)
    setStore(next)
    record({
      prevStore: prev, nextStore: next, action: "update", actor: "user",
      entityKind: kind, entityId: id, locale,
      summary: `Edited ${kind}`,
    })
  }, [setStore, record, locale])

  // An editor's Save: the record's draft replaces it as one change, one version
  const commitEntity = useCallback((kind: EntityKind, id: string, entity: any): boolean => {
    const prev = storeRef.current
    const before = db.getEntity(prev, kind, id)
    const bucket = BUCKET[kind]
    const next = { ...prev, [bucket]: { ...(prev as any)[bucket], [id]: { ...entity, updatedAt: Date.now() } } } as ServiceStore
    try { setStore(next) } catch { return false }
    record({
      prevStore: prev, nextStore: next, action: "update", actor: "user",
      entityKind: kind, entityId: id, locale,
      summary: describeEdit(before, entity),
    })
    return true
  }, [setStore, record, locale])

  const setTranslation = useCallback((kind: EntityKind, id: string, loc: string, patch: Record<string, any>) => {
    const prev = storeRef.current
    const next = db.setTranslation(prev, kind, id, loc, patch)
    setStore(next)
    record({
      prevStore: prev, nextStore: next, action: "translate", actor: "user",
      entityKind: kind, entityId: id, locale: loc,
      summary: `Translated ${kind} (${loc})`,
    })
  }, [setStore, record])

  const remove = useCallback((kind: EntityKind, id: string) => {
    const prev = storeRef.current
    const entityBeforeDelete = db.getEntity(prev, kind, id)
    const label = entityBeforeDelete ? displayName(entityBeforeDelete, locale, prev.defaultLocale) : id
    // Record before applying so the snapshot captures the still-intact entity.
    record({
      prevStore: prev, action: "delete", actor: "user",
      entityKind: kind, entityId: id, locale,
      summary: `Deleted ${kind} "${label}"`,
    })
    const { store: next } = db.deleteEntity(prev, kind, id)
    setStore(next)
    // A delete can take the current selection with it even when it wasn't the
    // direct target — deleting a country detaches (not deletes) its regions
    // and cities, but a service whose provider was removed can still lose its
    // own selection state elsewhere. Check the post-delete store rather than
    // just comparing ids to what was deleted.
    setSelected(cur => (cur && !db.getEntity(next, cur.kind, cur.id) ? null : cur))
  }, [locale, setStore, record])

  // Applies a batch of translations for one locale (used by the locale copy), one history entry per record
  const applyTranslations = useCallback((
    writes: Array<{ kind: EntityKind; id: string; patch: Record<string, any> }>,
    loc: string,
    summary: string,
  ): { applied: number } => {
    const before = storeRef.current
    const entries: Parameters<typeof recordMany>[0] = []
    const { store: next, applied } = db.setTranslationsBulk(
      before,
      writes.map(w => ({ kind: w.kind, id: w.id, locale: loc, patch: w.patch })),
      (w, beforeEntity) => {
        entries.push({
          action: "translate", actor: "user",
          entityKind: w.kind, entityId: w.id,
          entityName: displayName(beforeEntity, loc, before.defaultLocale),
          locale: loc, summary,
          before: beforeEntity,
        })
      },
    )
    if (!applied) return { applied: 0 }

    // Read after-snapshots once the whole batch is applied
    for (const e of entries) {
      const a = db.getEntity(next, e.entityKind, e.entityId)
      if (a) e.after = JSON.parse(JSON.stringify(a))
    }
    recordMany(entries)
    setStore(next)
    return { applied }
  }, [setStore, recordMany])

  const addLocale = useCallback((code: string, name?: string, direction: "ltr" | "rtl" = "ltr") => {
    // Adding an existing language again keeps its saved name and direction
    if (storeRef.current.languages[code]) return
    setStore(db.upsertLanguage(storeRef.current, { code, name: name || code, direction }))
  }, [setStore])

  const reset = useCallback(() => {
    setStore(emptyServiceStore(storeRef.current.defaultLocale || "en-US"))
    setSelected(null)
  }, [setStore])

  // ── History ──────────────────────────────────────────────────────────────

  const revert = useCallback((entryId: string): { ok: true; description: string } | { ok: false; error: string } => {
    const entry = historyRef.current.find(e => e.id === entryId)
    if (!entry) return { ok: false, error: `No history entry with id ${entryId}` }

    const result = revertEntry(storeRef.current, entry)
    if ("error" in result) return { ok: false, error: result.error }

    setStore(result.store)

    // Mark the original reverted, and log the revert itself so the trail is
    // complete — without this second entry the log shows a change greyed out
    // with no record of who undid it or when.
    const who = authorRef.current
    const marked = historyRef.current.map(e => e.id === entryId ? { ...e, reverted: true } : e)
    const withRevert = [...marked, makeEntry({
      action: "revert",
      actor: "user",
      authorName: who?.name,
      authorEmail: who?.email,
      authorId: who?.id,
      entityKind: entry.entityKind,
      entityId: entry.entityId,
      entityName: entry.entityName,
      locale: entry.locale,
      summary: result.description,
    })]
    historyRef.current = withRevert
    setHistoryState(withRevert)
    saveHistory(withRevert, dbIdRef.current)

    return { ok: true, description: result.description }
  }, [setStore])

  /** Version timeline for one entity, newest first. */
  const versionsOf = useCallback((entityId: string): EntityVersion[] =>
    versionsForEntity(historyRef.current, entityId), [])

  /**
   * Restores an entity to one of its recorded versions. Logged as an update,
   * so the restore is itself revertable.
   */
  const restoreEntityVersionAndRecord = useCallback((entityId: string, entryId: string, kind: EntityKind):
    { ok: true; description: string } | { ok: false; error: string } => {
    const version = versionsForEntity(historyRef.current, entityId)
      .find(v => v.entryId === entryId && !!v.entity)
    if (!version?.entity) return { ok: false, error: "That version has no saved snapshot to restore." }

    const prev = storeRef.current
    const next = restoreEntityVersion(prev, kind, version.entity)
    setStore(next)
    record({
      prevStore: prev, nextStore: next, action: "update", actor: "user",
      entityKind: kind, entityId, locale,
      summary: `Restored to version from ${new Date(version.at).toLocaleString()} (${version.author})`,
    })
    return { ok: true, description: `Restored to the ${new Date(version.at).toLocaleString()} version` }
  }, [setStore, record, locale])

  const wipeHistory = useCallback(() => {
    if (accessRef.current !== "manage") {
      toast.error("Only a manager can clear the change history.")
      return
    }
    clearHistory(dbIdRef.current)
    historyRef.current = []
    setHistoryState([])
  }, [])

  // ── Derived reads ────────────────────────────────────────────────────────

  const nameOf = useCallback((kind: EntityKind, id: string): string => {
    const e = db.getEntity(storeRef.current, kind, id)
    return e ? displayName(e, locale, storeRef.current.defaultLocale) : id
  }, [locale])

  return {
    /** Database sync state; "offline" means no team selected yet. */
    syncStatus,
    /** Unpublished changes: what's waiting, and the store as it's live. */
    pending: {
      available: staging.available,
      keys: staging.pendingKeys,
      // Includes changes React hasn't rendered yet
      allKeys: staging.allKeys,
      liveStore: staging.liveStore,
      keyOf: staging.keyOf,
      liveEntity: staging.liveEntity,
      canPublish: access === "manage",
      publish: publishPending,
      discard: discardPending,
    },
    /** True once this team's rows have loaded from the database. */
    loaded: !!dbId && loadedFor === dbId,
    flushSync: () => syncRef.current.flush(),
    store, setStore,
    /** Always-current store reader for tool closures. See storeRef above. */
    getStore: () => storeRef.current,
    locale, setLocale,
    locales: Object.keys(store.languages),
    selected, setSelected,
    create, update, setTranslation, commitEntity, remove, addLocale, reset,
    applyTranslations,
    nameOf,
    stats: db.stats(store),

    history,
    /** Always-current history reader for tool closures. */
    getHistory: () => historyRef.current,
    record,
    recordMany,
    recordBulk,
    revert,
    versionsOf,
    restoreEntityVersion: restoreEntityVersionAndRecord,
    wipeHistory,
  }
}
